import { useEffect } from 'react'
import {
  connectWebSocket,
  onExperimentCompleted,
  onExperimentReadinessChanged,
  onExperimentStatusChanged,
} from '@/lib/services/websocket'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'

interface UseExperimentListRealtimeOptions {
  active: boolean
  onInvalidate: () => void
}

/**
 * Keeps experiment/studies lobby lists fresh without SWR interval polling.
 * Listens for global experiment notifications and runs a stall repair while
 * any visible row is still mid-flight.
 */
export function useExperimentListRealtime({
  active,
  onInvalidate,
}: UseExperimentListRealtimeOptions) {
  const { notifyActivity } = useRealtimeRepair({
    active,
    onRepair: onInvalidate,
    stallTimeoutMs: 45_000,
  })

  useEffect(() => {
    connectWebSocket()
    const invalidate = () => {
      notifyActivity()
      onInvalidate()
    }
    const offStatus = onExperimentStatusChanged(invalidate)
    const offCompleted = onExperimentCompleted(invalidate)
    const offReadiness = onExperimentReadinessChanged(invalidate)
    return () => {
      offStatus()
      offCompleted()
      offReadiness()
    }
  }, [notifyActivity, onInvalidate])
}
